import { useEffect, useState } from 'react'
import { CoinMark, Logo, TreeMark } from '../Logo'
import { Reveal } from './Reveal'

const NAV_LINKS = [
  { href: '#work', label: 'Features' },
  { href: '#journey', label: 'Journey' },
  { href: '#pricing', label: 'Pricing' },
  { href: '#faq', label: 'FAQ' },
  { href: '/blog', label: 'Blog' },
]

/**
 * Chrome over the cinematic hero: the Rep·Tree wordmark top-left (cream over the photo,
 * ink once the page scrolls onto bone), paper pills floated top-right (A24 × Floema).
 * No theme toggle — the landing is light only. `onLight` forces the ink wordmark for pages
 * with a light background and no dark hero (e.g. the blog), where cream would vanish.
 */
export function MarketingChrome({ onLight = false }: { onLight?: boolean }) {
  const [overHero, setOverHero] = useState(!onLight)
  useEffect(() => {
    if (onLight) return
    const onScroll = () => setOverHero(window.scrollY < window.innerHeight * 0.72)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [onLight])

  // The chrome's in-page anchors (logo "#top", nav "#section") only exist on the landing. When this
  // chrome rides a different page (e.g. the blog, onLight), point them back at the landing instead:
  // "#top" → "/", "#section" → "/#section". Absolute hrefs (/blog, /login) are left untouched.
  const resolve = (href: string) =>
    !onLight || !href.startsWith('#') ? href : href === '#top' ? '/' : `/${href}`

  return (
    <>
      <a
        href={resolve('#top')}
        aria-label="RepTree home"
        className={`fixed left-5 top-5 z-50 transition-colors duration-500 md:left-9 md:top-7 ${overHero ? 'text-paper' : 'text-ink'}`}
      >
        <Logo className="text-2xl" />
      </a>
      <nav
        aria-label="Primary"
        className="fixed right-3 top-4 z-50 flex max-w-[78vw] items-center gap-1.5 overflow-x-auto md:right-7 md:top-6 [scrollbar-width:none]"
      >
        {NAV_LINKS.map((l) => (
          <a key={l.href} href={resolve(l.href)} className="pillnav-link hidden sm:inline-flex">
            {l.label}
          </a>
        ))}
        <a href="/login" className="pillnav-link">Sign in</a>
        <a href="/login?mode=signup" className="pillnav-link !border-transparent !bg-forest !text-paper hover:!brightness-110">
          Start free
        </a>
      </nav>
    </>
  )
}

const VOICES = [
  {
    quote: 'Every casting email used to mean re-typing the same audition into a spreadsheet. Now it just shows up, already tracked.',
    who: 'A talent manager',
    where: 'Los Angeles',
  },
  {
    quote: 'My clients stopped texting “any news?” the week I invited them. They can see the callback before I’ve even called.',
    who: 'A theatrical agent',
    where: 'New York',
  },
  {
    quote: 'Tapes that used to vanish into expired links live in one place now — I can pull a year-old take in two taps.',
    who: 'A boutique-agency owner',
    where: 'Atlanta',
  },
]

export function Testimonials() {
  return (
    <div className="grid gap-px overflow-hidden rounded-card border hairline bg-[color:rgb(var(--hairline)/0.1)] md:grid-cols-3">
      {VOICES.map((v, i) => (
        <Reveal key={v.who} delay={i * 90} className="flex flex-col bg-paper p-8 md:p-10">
          <span className="eyebrow">0{i + 1}</span>
          <p className="mt-5 flex-1 font-display text-xl leading-snug">“{v.quote}”</p>
          <p className="eyebrow mt-7">{v.who} — {v.where}</p>
        </Reveal>
      ))}
    </div>
  )
}

export function MarketingFooter() {
  const cols: { title: string; links: { label: string; href: string }[] }[] = [
    {
      title: 'Product',
      links: [
        { label: 'Features', href: '#work' },
        { label: 'Journey', href: '#journey' },
        { label: 'Pricing', href: '#pricing' },
        { label: 'FAQ', href: '#faq' },
        { label: 'Blog', href: '/blog' },
      ],
    },
    {
      title: 'Get started',
      links: [
        { label: 'Start free', href: '/login?mode=signup' },
        { label: 'Sign in', href: '/login' },
      ],
    },
    {
      title: 'Company',
      links: [
        { label: 'Studio Llama', href: 'https://studiollama.com' },
        { label: 'Contact', href: 'mailto:hello@reptree.io' },
      ],
    },
    {
      title: 'Legal',
      links: [
        { label: 'Terms of Service', href: '/terms' },
        { label: 'Privacy Policy', href: '/privacy' },
      ],
    },
  ]
  return (
    <footer className="relative overflow-hidden border-t hairline bg-bone">
      <div className="mx-auto max-w-[1320px] px-6 pb-10 pt-16 md:px-10">
        <div className="grid gap-12 md:grid-cols-[1.5fr_repeat(4,1fr)]">
          <div>
            <div className="flex items-center gap-3">
              <CoinMark className="h-12 w-12 text-ink" />
              <Logo className="text-xl" />
            </div>
            <p className="mt-5 max-w-xs text-[color:rgb(var(--muted))]">
              The home base for film &amp; TV talent agents and managers. Collect tapes, track every audition, keep clients in the loop.
            </p>
          </div>
          {cols.map((c) => (
            <div key={c.title}>
              <p className="eyebrow">{c.title}</p>
              <ul className="mt-5 space-y-2.5">
                {c.links.map((l) => (
                  <li key={l.label}>
                    <a href={l.href} className="transition hover:text-forest">{l.label}</a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="row-rule mt-14 flex flex-col items-center justify-between gap-3 pt-6 text-[color:rgb(var(--muted))] md:flex-row">
          <p className="eyebrow">© 2026 Studio Llama · RepTree</p>
          <p className="eyebrow flex items-center gap-2"><TreeMark className="h-4 w-4" /> reptree.io</p>
        </div>
      </div>
      {/* Giant ghost wordmark grounding the footer, Floema-style. */}
      <div className="pointer-events-none flex select-none justify-center px-2 pb-1 opacity-[0.06]" aria-hidden>
        <Logo className="display-giant whitespace-nowrap" />
      </div>
    </footer>
  )
}
